import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
    Chart as ChartJS,
    BarElement,
    ArcElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
} from "chart.js";

import { Line, Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
    BarElement,
    ArcElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
);

// export const THEME = {
//     bg: "#0b1220",          // main background
//     bgSoft: "#0f1a33",      // cards
//     primary: "#5e72e4",     // Argon blue
//     accent: "#11cdef",      // info / coverage
//     success: "#2dce89",     // good performance
//     warning: "#fb6340",     // risk
//     danger: "#f5365c",      // high burden
//     textMain: "#ffffff",
//     textMuted: "#94a3b8",
//     border: "rgba(255,255,255,0.08)",
// };
export const THEME = {
    bg: "#0E1628",            // Deep navy background
    bgCard: "#131E33",        // Card surface
    bgHover: "#182544",

    primary: "#4F8CFF",       // Professional Blue
    cyan: "#22D3EE",          // Info tone
    green: "#22C55E",         // Positive
    amber: "#F59E0B",         // Warning
    red: "#EF4444",           // Critical
    violet: "#8B5CF6",        // Secondary accent
    pink: "#EC4899",          // Highlight metric

    textMain: "#F1F5F9",
    textMuted: "#94A3B8",
    grid: "rgba(148,163,184,0.08)",
    border: "rgba(148,163,184,0.15)"
};
export const GRAPH_COLORS = {
    blue: "#4F8CFF",
    blueSoft: "rgba(79,140,255,0.18)",

    teal: "#14B8A6",
    tealSoft: "rgba(20,184,166,0.18)",

    purple: "#8B5CF6",
    purpleSoft: "rgba(139,92,246,0.18)",

    amber: "#F59E0B",
    amberSoft: "rgba(245,158,11,0.18)",

    slate: "#64748B",
    slateSoft: "rgba(100,116,139,0.18)",

    green: "#22C55E",
    greenSoft: "rgba(34,197,94,0.18)"
};
/* ================= SCROLL ANIMATION WRAPPER ================= */

const AnimatedBlock = ({ children, direction = "left", delay = 0 }) => {
    const { ref, inView } = useInView({
        triggerOnce: false,
        threshold: 0.5,
    });

    const variants = {
        hidden: {
            opacity: 0,
            x: direction === "left" ? -80 : 80,
        },
        visible: {
            opacity: 1,
            x: 0,
        },
    };

    return (
        <motion.div
            ref={ref}
            variants={variants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            transition={{
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1], // smoother easing
                delay,
            }}
        >
            {children}
        </motion.div>
    );
};

/* ================= HERO SECTION ================= */

const HeroOverview = () => {
    return (
        <section
            className="
              h-screen 
              flex items-center justify-center 
              bg-gradient-to-b from-[#172b4d] to-black 
              text-white 
              snap-start   /* 👈 ADD THIS */
            "
        >
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center max-w-5xl"
            >
                <h1 className="text-5xl font-bold mb-6">
                    Maharashtra Health Programs Dashboard
                </h1>
                <p className="text-xl text-gray-300">
                    Scroll to explore program-wise performance & outcomes
                </p>
            </motion.div>
        </section>
    );
};

/* ================= SCROLL SECTION ================= justify-center*/
const ScrollSection = ({ title, description, children }) => {
    return (
        <section className="h-screen flex items-center snap-start p-20">
            <div className="max-w-10xl mx-auto w-full h-full flex flex-col ">
                <h2 className="text-3xl font-bold text-white mb-2">
                    {title}
                </h2>
                <p className="text-slate-400 mb-8">
                    {description}
                </p>
                {children}
            </div>
        </section>
    );
};



/* ================= KPI CARD ================= */

const KpiCard = ({ label, value, compact }) => (
    <div
        className={`rounded-xl border backdrop-blur-md
      ${compact ? "p-4" : "p-6"}`}
        style={{
            background: THEME.bgCard,
            borderColor: THEME.border,
        }}
    >
        <p className="text-xs text-slate-400">{label}</p>
        <p className="text-2xl font-bold text-white mt-1">
            {value}
        </p>
    </div>
);

const anemiaCharts = {
    screening: {
        labels: [
            "6–59 months",
            "5–9 yrs",
            "10–19 yrs",
            "Women (15–49)",
            "Pregnant Women",
        ],
        datasets: [
            {
                label: "Screening Coverage (%)",
                data: [82, 74, 69, 78, 88],
                backgroundColor: GRAPH_COLORS.blue,
                borderRadius: 8,
            },
        ],
    },

    severity: {
        labels: ["Normal", "Mild", "Moderate", "Severe"],
        datasets: [
            {
                data: [54, 27, 15, 4],
                backgroundColor: [
                    GRAPH_COLORS.green,     // Normal
                    GRAPH_COLORS.amber,     // Mild
                    "#FB923C",              // Moderate (soft orange)
                    GRAPH_COLORS.slate,     // Severe (neutral, not panic red)
                ],
            },
        ],
    },

    districts: {
        labels: [
            "Gadchiroli",
            "Nandurbar",
            "Palghar",
            "Osmanabad",
            "Beed",
        ],
        datasets: [
            {
                label: "Moderate–Severe Anemia (%)",
                data: [34, 31, 29, 27, 25],
                backgroundColor: GRAPH_COLORS.teal,
                borderRadius: 6,
            },
        ],
    },

    ifa: {
        labels: ["Target", "Achieved"],
        datasets: [
            {
                data: [100, 78],
                backgroundColor: [
                    GRAPH_COLORS.slate,
                    GRAPH_COLORS.blue
                ],
                borderRadius: 10,
            },
        ],
    },

    trend: {
        labels: ["2021", "2022", "2023", "2024", "2025"],
        datasets: [
            {
                label: "Anemia Prevalence (%)",
                data: [52, 50, 47, 44, 41],
                borderColor: GRAPH_COLORS.purple,
                backgroundColor: GRAPH_COLORS.purpleSoft,
                tension: 0.4,
                fill: true
            }
        ]
    },

    blockPerformance: {
        labels: ["Good (<15%)", "Moderate (15–25%)", "High (>25%)"],
        datasets: [
            {
                data: [142, 98, 37],
                backgroundColor: [
                    GRAPH_COLORS.green,
                    GRAPH_COLORS.amber,
                    GRAPH_COLORS.slate
                ]
            }
        ]
    },

    gap: {
        labels: ["Screened", "Diagnosed", "Treatment Started"],
        datasets: [
            {
                label: "Population (Lakhs)",
                data: [240, 92, 78],
                backgroundColor: [
                    GRAPH_COLORS.blue,
                    GRAPH_COLORS.teal,
                    GRAPH_COLORS.green
                ]
            }
        ]
    },

    adolescentTrend: {
        labels: ["2021", "2022", "2023", "2024", "2025"],
        datasets: [
            {
                label: "Adolescent Girls Anemia (%)",
                data: [44, 42, 40, 39, 38],
                borderColor: GRAPH_COLORS.teal,
                backgroundColor: GRAPH_COLORS.tealSoft,
                tension: 0.4,
                fill: true
            }
        ]
    },

    stockOut: {
        labels: [
            "District Hospital",
            "CHC",
            "PHC",
            "Sub-Center"
        ],
        datasets: [
            {
                label: "IFA Stock Out (%)",
                data: [4, 9, 14, 22],
                backgroundColor: GRAPH_COLORS.amber,
                borderRadius: 8
            }
        ]
    },

    supplyAvailability: {
        labels: [
            "IFA Tablets Available",
            "Hb Testing Kits Available"
        ],
        datasets: [
            {
                label: "Availability (%)",
                data: [86, 72],
                backgroundColor: [
                    GRAPH_COLORS.green,
                    GRAPH_COLORS.blue
                ],
                borderRadius: 8
            }
        ]
    },

    supplyCombined: {
        labels: ["IFA Tablets", "Syrup", "HB Kits", "Test Strips"],
        datasets: [
            {
                label: "Available (%)",
                data: [82, 75, 68, 90],
                backgroundColor: GRAPH_COLORS.blue,
            },
            {
                label: "Required (%)",
                data: [100, 100, 100, 100],
                backgroundColor: GRAPH_COLORS.slate,
            },
        ],
    },
};
const DarkChart = ({ title, children }) => (
    <div className="rounded-xl p-4 border h-[240px]"
        style={{
            background: THEME.bgCard,
            borderColor: THEME.border
        }}>
        <h4 className="text-sm font-semibold text-white mb-2">{title}</h4>
        <div className="h-[180px]">{children}</div>
    </div>
);
export const darkBarOptions = {
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        datalabels: { display: false }
    },
    elements: {
        bar: {
            borderRadius: {
                topLeft: 12,
                topRight: 12
            },
            borderSkipped: 'bottom'   // 👈 keeps bottom flat
        }
    },
    scales: {
        x: {
            ticks: { color: "#94a3b8" },
            grid: { color: "rgba(255,255,255,0.05)" }
        },
        y: {
            ticks: { color: "#94a3b8" },
            grid: { display: false }
        }
    }
};

// export const darkBarOptions = {
//     maintainAspectRatio: false,
//     plugins: { datalabels: { display: false }, legend: { display: false } },
//     scales: {
//         x: { ticks: { color: "#94a3b8" }, grid: { color: "rgba(255,255,255,0.05)" } },
//         y: { ticks: { color: "#94a3b8" }, grid: { color: "rgba(255,255,255,0.05)" } },
//     },
// };

export const darkHorizontalBarOptions = {
    ...darkBarOptions,
    indexAxis: "y",
};

export const darkLineOptions = {
    maintainAspectRatio: false,
    plugins: {
        datalabels: { display: false }, legend: { display: false }
    },
    scales: darkBarOptions.scales,
};

export const darkDoughnutOptions = {
    maintainAspectRatio: false,
    cutout: "70%",
    plugins: {
        datalabels: { display: false },
        legend: {
            position: "right",
            labels: { color: "#cbd5f5" },
        },
    },
};

/* ================= PROGRAM SECTIONS ================= */

const AnemiaSection = () => {
    return (
        <ScrollSection
            title="Anemia Control Program"
            description="State-wide screening, severity distribution and program performance indicators for Maharashtra."
            className="min-h-screen overflow-hidden"
        >

            {/* ================= KPI ROW ================= */}
            <AnimatedBlock direction="bottom">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3 mb-4">
                    <KpiCard label="Population Screened" value="2.4 Cr" compact />
                    <KpiCard label="Moderate–Severe Anemia" value="18.6%" compact />
                    <KpiCard label="Severe Anemia" value="4.2%" compact />
                    <KpiCard label="Pregnant Women Anemic" value="22%" compact />
                    <KpiCard label="Children (6–59m)" value="31%" compact />
                    <KpiCard label="IFA Coverage" value="78%" compact />
                    <KpiCard label="Adolescent Girls (10–19 yrs) Anemic" value="38%" compact />
                </div>
            </AnimatedBlock>


            {/* ================= CHART GRID ================= */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">

                {/* 1. Screening */}
                <AnimatedBlock direction="left" delay={0.15}>
                    <div className="rounded-xl p-4 border h-[210px]"
                        style={{
                            background: THEME.bgCard,
                            borderColor: THEME.border
                        }}>
                        <h4 className="text-sm font-semibold text-white mb-2">
                            Screening Coverage by Age Group
                        </h4>
                        <div className="h-[150px]">
                            <Bar data={anemiaCharts.screening} options={darkBarOptions} />
                        </div>
                    </div>
                </AnimatedBlock>


                {/* 2. Trend */}
                <AnimatedBlock direction="left" delay={0.2}>
                    <div className="rounded-xl p-4 border h-[210px]"
                        style={{
                            background: THEME.bgCard,
                            borderColor: THEME.border
                        }}>
                        <h4 className="text-sm font-semibold text-white mb-2">
                            Anemia Prevalence Trend
                        </h4>
                        <div className="h-[150px]">
                            <Line data={anemiaCharts.trend} options={darkLineOptions} />
                        </div>
                    </div>
                </AnimatedBlock>


                {/* 3. Severity */}
                <AnimatedBlock direction="left" delay={0.25}>
                    <div className="rounded-xl p-4 border h-[210px]"
                        style={{
                            background: THEME.bgCard,
                            borderColor: THEME.border
                        }}>
                        <h4 className="text-sm font-semibold text-white mb-2">
                            Severity Distribution
                        </h4>
                        <div className="h-[150px] flex items-center justify-center">
                            <Doughnut data={anemiaCharts.severity} options={darkDoughnutOptions} />
                        </div>
                    </div>
                </AnimatedBlock>


                {/* 4. Adolescent */}
                <AnimatedBlock direction="left" delay={0.3}>
                    <div className="rounded-xl p-4 border h-[210px]"
                        style={{
                            background: THEME.bgCard,
                            borderColor: THEME.border
                        }}>
                        <h4 className="text-sm font-semibold text-white mb-2">
                            Adolescent Girls Trend
                        </h4>
                        <div className="h-[150px]">
                            <Line data={anemiaCharts.adolescentTrend} options={darkLineOptions} />
                        </div>
                    </div>
                </AnimatedBlock>


                {/* 5. Gap */}
                <AnimatedBlock direction="right" delay={0.15}>
                    <div className="rounded-xl p-4 border h-[210px]"
                        style={{
                            background: THEME.bgCard,
                            borderColor: THEME.border
                        }}>
                        <h4 className="text-sm font-semibold text-white mb-2">
                            Screening vs Treatment
                        </h4>
                        <div className="h-[150px]">
                            <Bar data={anemiaCharts.gap} options={darkBarOptions} />
                        </div>
                    </div>
                </AnimatedBlock>


                {/* 6. Districts */}
                <AnimatedBlock direction="right" delay={0.2}>
                    <div className="rounded-xl p-4 border h-[210px]"
                        style={{
                            background: THEME.bgCard,
                            borderColor: THEME.border
                        }}>
                        <h4 className="text-sm font-semibold text-white mb-2">
                            High Burden Districts
                        </h4>
                        <div className="h-[150px]">
                            <Bar
                                data={anemiaCharts.districts}
                                options={{ ...darkBarOptions, indexAxis: "y" }}
                            />
                        </div>
                    </div>
                </AnimatedBlock>


                {/* 7. IFA */}
                <AnimatedBlock direction="right" delay={0.25}>
                    <div className="rounded-xl p-4 border h-[210px]"
                        style={{
                            background: THEME.bgCard,
                            borderColor: THEME.border
                        }}>
                        <h4 className="text-sm font-semibold text-white mb-2">
                            IFA Coverage vs Target
                        </h4>
                        <div className="h-[150px]">
                            <Bar data={anemiaCharts.ifa} options={darkBarOptions} />
                        </div>
                    </div>
                </AnimatedBlock>


                {/* 8. Supply */}
                <AnimatedBlock direction="right" delay={0.3}>
                    <div className="rounded-xl p-4 border h-[210px]"
                        style={{
                            background: THEME.bgCard,
                            borderColor: THEME.border
                        }}>
                        <h4 className="text-sm font-semibold text-white mb-2">
                            Supply Chain Status
                        </h4>
                        <div className="h-[150px]">
                            <Bar data={anemiaCharts.supplyCombined} options={darkBarOptions} />
                        </div>
                    </div>
                </AnimatedBlock>

            </div>
        </ScrollSection>
    );
};

const tbCharts = {
    trend: {
        labels: ["2021", "2022", "2023", "2024", "2025"],
        datasets: [
            {
                label: "TB Notifications (Lakh)",
                data: [2.1, 1.9, 1.7, 1.8, 1.8],
                borderColor: GRAPH_COLORS.blue,
                backgroundColor: GRAPH_COLORS.blueSoft,
                tension: 0.4,
                fill: true,
            },
        ],
    },

    districts: {
        labels: ["Mumbai", "Thane", "Pune", "Nagpur", "Nashik"],
        datasets: [
            {
                label: "Notification Rate",
                data: [285, 248, 232, 221, 210],
                backgroundColor: GRAPH_COLORS.teal,
                borderRadius: 6,
            },
        ],
    },

    outcomes: {
        labels: ["Success", "Death", "LTFU", "Transferred"],
        datasets: [
            {
                data: [87, 6, 4, 3],
                backgroundColor: [
                    GRAPH_COLORS.green,   // Success
                    GRAPH_COLORS.slate,   // Death (neutral, not dramatic red)
                    GRAPH_COLORS.amber,   // LTFU
                    GRAPH_COLORS.blue,    // Transferred
                ],
            },
        ],
    },

    sector: {
        labels: ["Public", "Private"],
        datasets: [
            {
                data: [62, 38],
                backgroundColor: [
                    GRAPH_COLORS.blue,
                    GRAPH_COLORS.teal
                ],
            },
        ],
    },

    drugResistance: {
        labels: ["RR-TB", "MDR-TB", "XDR-TB"],
        datasets: [
            {
                label: "Percentage",
                data: [3.2, 1.4, 0.3],
                backgroundColor: GRAPH_COLORS.amber,
                borderRadius: 6,
                barThickness: 30,
            },
        ],
    },

    screeningGap: {
        labels: ["Screened (Lakhs)", "Confirmed TB (Lakhs)", "Positivity Rate (%)"],
        datasets: [
            {
                label: "Value",
                data: [52, 1.8, 3.5],
                backgroundColor: [
                    GRAPH_COLORS.blue,    // screened
                    GRAPH_COLORS.teal,    // confirmed
                    GRAPH_COLORS.amber    // positivity
                ],
                borderRadius: 12,
                borderSkipped: false
            }
        ]
    },

    ageDistribution: {
        labels: ["0–14", "15–24", "25–44", "45–64", "65+"],
        datasets: [
            {
                label: "Cases (in thousands)",
                data: [12, 28, 62, 54, 24],
                backgroundColor: GRAPH_COLORS.purple,
                borderRadius: 12,
                borderSkipped: false
            }
        ]
    }
};
const TbSection = () => {
    return (
        <ScrollSection
            title="TB Elimination Program"
            description="Tuberculosis notification, treatment initiation and outcomes for Maharashtra."
        >

            {/* KPI ROW – SLIDE UP */}
            <AnimatedBlock direction="top">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                    <KpiCard label="TB Notifications" value="1.8 Lakh" compact />
                    <KpiCard label="Treatment Initiation" value="91%" compact />
                    <KpiCard label="Treatment Success" value="87%" compact />
                    <KpiCard label="TB Mortality Rate" value="4.8%" compact />
                    <KpiCard label="Drug Resistant TB" value="6.2%" compact />
                    <KpiCard label="Private Sector Share" value="28%" compact />
                </div>
            </AnimatedBlock>


            {/* CHARTS GRID */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

                {/* Notification Trend – LEFT */}
                <AnimatedBlock direction="left" delay={0.15}>
                    <div
                        className="rounded-xl p-4 border h-[240px]"
                        style={{
                            background: "rgba(255,255,255,0.04)",
                            borderColor: "rgba(255,255,255,0.08)",
                        }}
                    >
                        <h4 className="text-sm font-semibold text-white mb-2">
                            TB Case Notification Trend
                        </h4>
                        <div className="h-[180px]">
                            <Line
                                data={tbCharts.trend}
                                options={darkLineOptions}
                            />
                        </div>
                    </div>
                </AnimatedBlock>
                <AnimatedBlock direction="left" delay={0.15}>
                    <div className="rounded-xl p-4 border h-[240px]"
                        style={{
                            background: "rgba(255,255,255,0.04)",
                            borderColor: "rgba(255,255,255,0.08)",
                        }}
                    >
                        <h4 className="text-sm font-semibold text-white mb-2">
                            Screening vs Positivity Rate
                        </h4>
                        <div className="h-[180px]">
                            <Bar
                                data={tbCharts.screeningGap}
                                options={darkBarOptions}
                            />
                        </div>
                    </div>
                </AnimatedBlock>


                {/* Outcome – RIGHT */}
                <AnimatedBlock direction="left" delay={0.15}>
                    <div
                        className="rounded-xl p-4 border h-[240px]"
                        style={{
                            background: "rgba(255,255,255,0.04)",
                            borderColor: "rgba(255,255,255,0.08)",
                        }}
                    >
                        <h4 className="text-sm font-semibold text-white mb-2">
                            Treatment Outcome Distribution
                        </h4>
                        <div className="h-[180px] flex justify-center">
                            <Doughnut
                                data={tbCharts.outcomes}
                                options={darkDoughnutOptions}
                            />
                        </div>
                    </div>
                </AnimatedBlock>

                {/* High Burden Districts – LEFT */}
                <AnimatedBlock direction="right" delay={0.15}>
                    <div
                        className="rounded-xl p-4 border h-[240px]"
                        style={{
                            background: "rgba(255,255,255,0.04)",
                            borderColor: "rgba(255,255,255,0.08)",
                        }}
                    >
                        <h4 className="text-sm font-semibold text-white mb-2">
                            High Burden Districts
                        </h4>
                        <div className="h-[180px]">
                            <Bar
                                data={tbCharts.districts}
                                options={{
                                    maintainAspectRatio: false,
                                    indexAxis: "y",
                                    plugins: { datalabels: { display: false }, legend: { display: false } },
                                    scales: {
                                        x: {
                                            ticks: { color: "#94a3b8" },
                                            grid: { color: "rgba(255,255,255,0.05)" },
                                        },
                                        y: {
                                            ticks: { color: "#94a3b8" },
                                            grid: { display: false },
                                        },
                                    },
                                }}
                            />

                            {/* <Bar
                                data={tbCharts.districts}
                                options={darkHorizontalBarOptions}
                            /> */}
                        </div>
                    </div>
                </AnimatedBlock>

                {/* Drug Resistance – RIGHT */}
                <AnimatedBlock direction="right" delay={0.15}>
                    <div
                        className="rounded-xl p-4 border h-[240px]"
                        style={{
                            background: "rgba(255,255,255,0.04)",
                            borderColor: "rgba(255,255,255,0.08)",
                        }}
                    >
                        <h4 className="text-sm font-semibold text-white mb-2">
                            Drug-Resistant TB (%)
                        </h4>
                        <div className="h-[180px]">
                            <Bar
                                data={tbCharts.drugResistance}
                                options={darkBarOptions}
                            />
                        </div>
                    </div>
                </AnimatedBlock>
                <AnimatedBlock direction="right">
                    <div className="rounded-xl p-4 border h-[240px]"
                        style={{
                            background: "rgba(255,255,255,0.04)",
                            borderColor: "rgba(255,255,255,0.08)",
                        }}
                    >
                        <h4 className="text-sm font-semibold text-white mb-2">
                            TB Cases by Age Group
                        </h4>
                        <div className="h-[180px]">
                            <Bar
                                data={tbCharts.ageDistribution}
                                options={darkBarOptions}
                            />
                        </div>
                    </div>
                </AnimatedBlock>

            </div>
        </ScrollSection>
    );
};
const ncdCharts = {
    screening: {
        labels: ["30–44 yrs", "45–59 yrs", "60+ yrs"],
        datasets: [{
            data: [72, 81, 68],
            backgroundColor: GRAPH_COLORS.purple,
        }],
    },

    burden: {
        labels: ["Hypertension", "Diabetes", "Both"],
        datasets: [{
            data: [52, 28, 20],
            backgroundColor: [
                GRAPH_COLORS.blue,      // Hypertension
                GRAPH_COLORS.teal,      // Diabetes
                GRAPH_COLORS.purple     // Both
            ],
        }],
    },

    districts: {
        labels: ["Nanded", "Solapur", "Beed", "Jalgaon", "Amravati"],
        datasets: [{
            data: [29, 27, 26, 25, 24],
            backgroundColor: GRAPH_COLORS.amber,
            borderRadius: 8,
        }],
    },

    treatment: {
        labels: ["Hypertension", "Diabetes"],
        datasets: [{
            data: [84, 79],
            backgroundColor: [
                GRAPH_COLORS.green,
                GRAPH_COLORS.blue
            ],
        }],
    },

    gender: {
        labels: ["Male", "Female"],
        datasets: [
            {
                label: "Hypertension (%)",
                data: [23, 20],
                backgroundColor: GRAPH_COLORS.purple,
                borderRadius: 12,
                borderSkipped: false
            },
            {
                label: "Diabetes (%)",
                data: [10.5, 9.1],
                backgroundColor: GRAPH_COLORS.teal,
                borderRadius: 12,
                borderSkipped: false
            }
        ]
    },

    trend: {
        labels: ["2021", "2022", "2023", "2024", "2025"],
        datasets: [
            {
                label: "Hypertension (%)",
                data: [19.8, 20.3, 20.9, 21.1, 21.4],
                borderColor: GRAPH_COLORS.purple,
                backgroundColor: GRAPH_COLORS.purpleSoft,
                tension: 0.4,
                fill: true
            },
            {
                label: "Diabetes (%)",
                data: [8.5, 8.9, 9.2, 9.5, 9.8],
                borderColor: GRAPH_COLORS.teal,
                backgroundColor: GRAPH_COLORS.tealSoft,
                tension: 0.4,
                fill: true
            }
        ]
    },

    mortality: {
        labels: ["2021", "2022", "2023", "2024", "2025"],
        datasets: [
            {
                label: "NCD Mortality Rate (per 100k)",
                data: [145, 148, 152, 158, 155],
                borderColor: GRAPH_COLORS.amber,
                backgroundColor: GRAPH_COLORS.amberSoft,
                tension: 0.4,
                fill: true,
            },
        ],
    },
};
export const rmnchaCharts = {
    ancTrend: {
        labels: ["2021", "2022", "2023", "2024", "2025"],
        datasets: [
            {
                data: [78, 72, 76, 80, 70],
                borderColor: GRAPH_COLORS.blue,
                backgroundColor: GRAPH_COLORS.blueSoft,
                tension: 0.4,
                fill: true,
            },
        ],
    },

    deliveryType: {
        labels: ["Normal", "C-Section"],
        datasets: [
            {
                data: [74, 26],
                backgroundColor: [
                    GRAPH_COLORS.green,   // Normal delivery
                    GRAPH_COLORS.teal     // C-Section
                ],
            },
        ],
    },

    districts: {
        labels: ["Gadchiroli", "Nandurbar", "Palghar", "Washim"],
        datasets: [
            {
                data: [78, 72, 69, 65],
                backgroundColor: GRAPH_COLORS.purple,
                borderRadius: 8,
            },
        ],
    },

    immunization: {
        labels: ["Coverage"],
        datasets: [
            {
                data: [91],
                backgroundColor: GRAPH_COLORS.teal,
                borderRadius: 8,
            },
        ],
    },

    mortalityTrend: {
        labels: ["2021", "2022", "2023", "2024", "2025"],
        datasets: [
            {
                label: "MMR (per 100k)",
                data: [58, 56, 54, 53, 52],
                borderColor: GRAPH_COLORS.amber,
                backgroundColor: GRAPH_COLORS.amberSoft,
                tension: 0.4,
                fill: true
            },
            {
                label: "IMR (per 1k)",
                data: [21, 20, 19, 18, 17],
                borderColor: GRAPH_COLORS.blue,
                backgroundColor: GRAPH_COLORS.blueSoft,
                tension: 0.4,
                fill: true
            }
        ]
    },

    cascade: {
        labels: ["Registered ANC", "4+ ANC", "Institutional Delivery", "PNC within 48 hrs"],
        datasets: [
            {
                label: "Coverage (%)",
                data: [82, 74, 96, 71],
                backgroundColor: GRAPH_COLORS.purple,
                borderRadius: 12,
                borderSkipped: false
            }
        ]
    },

    neonatalTrend: {
        labels: ["2021", "2022", "2023", "2024", "2025"],
        datasets: [
            {
                label: "Neonatal Mortality Rate",
                data: [18, 17, 16, 15, 14],
                borderColor: GRAPH_COLORS.teal,
                backgroundColor: GRAPH_COLORS.tealSoft,
                tension: 0.4,
                fill: true,
            },
        ],
    },
};

const RmnchaSection = () => {
    return (
        <ScrollSection
            title="RMNCHA Program"
            description="Maternal, newborn, child and adolescent health indicators for Maharashtra."
        >

            {/* KPI ROW – SLIDE UP */}
            <AnimatedBlock direction="bottom">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3 mb-5">
                    <KpiCard label="Early ANC Registration" value="82%" compact />
                    <KpiCard label="4+ ANC Visits" value="74%" compact />
                    <KpiCard label="Institutional Delivery" value="96%" compact />
                    <KpiCard label="C-Section Rate" value="19%" compact />
                    <KpiCard label="Full Immunization" value="91%" compact />
                    <KpiCard label="Low Birth Weight" value="14%" compact />
                    <KpiCard label="Adolescent Anemia" value="28%" compact />
                    <KpiCard label="High-Risk Pregnancy Identified" value="12%" compact />
                    <KpiCard label="MMR (Proxy)" value="52" compact />
                </div>
            </AnimatedBlock>


            {/* CHARTS GRID */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">

                {/* ANC Trend – LEFT */}
                <AnimatedBlock direction="left" delay={0.15}>
                    <DarkChart title="ANC Coverage Trend (%)">
                        <Line
                            data={rmnchaCharts.ancTrend}
                            options={darkLineOptions}
                        />
                    </DarkChart>
                </AnimatedBlock>
                <AnimatedBlock direction="left" delay={0.15}>
                    <DarkChart title="MMR & IMR Trend (5 Years)">
                        <Line
                            data={rmnchaCharts.mortalityTrend}
                            options={darkLineOptions}
                        />
                    </DarkChart>
                </AnimatedBlock>

                {/* Delivery Type – RIGHT */}
                <AnimatedBlock direction="left" delay={0.15}>
                    <DarkChart title="Delivery Type Distribution">
                        <Doughnut
                            data={rmnchaCharts.deliveryType}
                            options={darkDoughnutOptions}
                        />
                    </DarkChart>
                </AnimatedBlock>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                {/* High MMR Districts – LEFT */}
                <AnimatedBlock direction="right" delay={0.15}>
                    <DarkChart title="High MMR Districts">
                        <Bar
                            data={rmnchaCharts.districts}
                            options={{
                                maintainAspectRatio: false,
                                indexAxis: "y",
                                plugins: { datalabels: { display: false }, legend: { display: false } },
                                scales: {
                                    x: {
                                        ticks: { color: "#94a3b8" },
                                        grid: { color: "rgba(255,255,255,0.05)" },
                                    },
                                    y: {
                                        ticks: { color: "#94a3b8" },
                                        grid: { display: false },
                                    },
                                },
                            }}
                        />
                        {/* <Bar
                            data={rmnchaCharts.districts}
                            options={darkHorizontalBarOptions}
                        /> */}
                    </DarkChart>
                </AnimatedBlock>

                {/* Immunization – RIGHT */}
                <AnimatedBlock direction="right" delay={0.15}>
                    <DarkChart title="Immunization Coverage">
                        <Bar
                            data={rmnchaCharts.immunization}
                            options={darkBarOptions}
                        />
                    </DarkChart>
                </AnimatedBlock>
                <AnimatedBlock direction="right" delay={0.15}>
                    <DarkChart title="Maternal Care Cascade">
                        <Bar
                            data={rmnchaCharts.cascade}
                            options={darkBarOptions}
                        />
                    </DarkChart>
                </AnimatedBlock>
                <AnimatedBlock direction="left" delay={0.2}>
                    <DarkChart title="Neonatal Mortality Rate Trend (per 1000)">
                        <Line
                            data={rmnchaCharts.neonatalTrend}
                            options={darkLineOptions}
                        />
                    </DarkChart>
                </AnimatedBlock>

            </div>
        </ScrollSection>
    );
};

const NcdSection = () => {
    return (
        <ScrollSection
            title="Non-Communicable Disease (NCD) Program"
            description="Screening, disease burden and treatment performance for Maharashtra."
        >

            {/* KPI ROW – SLIDE UP */}
            <AnimatedBlock direction="bottom">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                    <KpiCard label="Population Screened (30+)" value="3.1 Cr" compact />
                    <KpiCard label="Hypertension Prevalence" value="21.4%" compact />
                    <KpiCard label="Diabetes Prevalence" value="9.8%" compact />
                    <KpiCard label="On Treatment (HTN)" value="68%" compact />
                    <KpiCard label="On Treatment (DM)" value="61%" compact />
                    <KpiCard label="Controlled Cases" value="54%" compact />
                </div>
            </AnimatedBlock>


            {/* CHARTS GRID */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
                {/* Screening Coverage – LEFT */}
                <AnimatedBlock direction="left" delay={0.15}>
                    <DarkChart title="Screening Coverage by Age">
                        <Bar
                            data={ncdCharts.screening}
                            options={darkBarOptions}
                        />
                    </DarkChart>
                </AnimatedBlock>
                <AnimatedBlock direction="left" delay={0.15}>
                    <DarkChart title="Hypertension & Diabetes Trend">
                        <Line
                            data={ncdCharts.trend}
                            options={darkLineOptions}
                        />
                    </DarkChart>
                </AnimatedBlock>

                {/* Disease Burden – RIGHT */}
                <AnimatedBlock direction="left" delay={0.15}>
                    <DarkChart title="Disease Burden Distribution">
                        <Doughnut
                            data={ncdCharts.burden}
                            options={darkDoughnutOptions}
                        />
                    </DarkChart>
                </AnimatedBlock>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mt-3">
                {/* High Burden Districts – LEFT */}
                <AnimatedBlock direction="right" delay={0.15}>
                    <DarkChart title="High Burden Districts">
                        <Bar
                            data={ncdCharts.districts}
                            options={{
                                maintainAspectRatio: false,
                                indexAxis: "y",
                                plugins: { datalabels: { display: false }, legend: { display: false } },
                                scales: {
                                    x: {
                                        ticks: { color: "#94a3b8" },
                                        grid: { color: "rgba(255,255,255,0.05)" },
                                    },
                                    y: {
                                        ticks: { color: "#94a3b8" },
                                        grid: { display: false },
                                    },
                                },
                            }}
                        />

                        {/* <Bar
                            data={ncdCharts.districts}
                            options={darkHorizontalBarOptions}
                        /> */}
                    </DarkChart>
                </AnimatedBlock>

                {/* Treatment Coverage – RIGHT */}
                <AnimatedBlock direction="right" delay={0.15}>
                    <DarkChart title="Treatment Coverage">
                        <Bar
                            data={ncdCharts.treatment}
                            options={darkBarOptions}
                        />
                    </DarkChart>
                </AnimatedBlock>
                <AnimatedBlock direction="right" delay={0.15}>
                    <DarkChart title="Gender-wise Disease Distribution">
                        <Bar
                            data={ncdCharts.gender}
                            options={darkBarOptions}
                        />
                    </DarkChart>
                </AnimatedBlock>
                <AnimatedBlock direction="right" delay={0.2}>
                    <DarkChart title="NCD Mortality Trend">
                        <Line
                            data={ncdCharts.mortality}
                            options={darkLineOptions}
                        />
                    </DarkChart>
                </AnimatedBlock>

            </div>
        </ScrollSection>
    );
};

/* ================= DASHBOARD ================= */
export default function MaharashtraProgramsDashboard() {
    return (
        <div
            className="
              maharashtra-dashboard
              h-screen
              overflow-y-auto
              overflow-x-hidden   /* 👈 IMPORTANT FIX */
              scroll-smooth
              snap-y snap-mandatory
            "
        >
            <div className="bg-gradient-to-b from-[#0b1220] via-[#0f1a33] to-black text-white">
                <HeroOverview />
                <AnemiaSection />
                <TbSection />
                <NcdSection />
                <RmnchaSection />
            </div>
        </div>
    );
}


